import React, { createContext, useCallback, useContext, useEffect } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as ArrowLeft } from '@pkg/assets/icons/v2/arrow-left.svg';
import { Sheet } from '@pkg/components';
import { ButtonBack } from '@pkg/components/Sections/Profile/Buttons';
import { useAppDispatch } from '@pkg/hooks';
import { TwoFactorAuthType } from '@pkg/types/enums';

import { type Token } from '@app/client/common/structs';
import { callGenericProviderSignIn, callTwitterProviderSignIn } from '@app/client/resources/w3auth';
import { AuthProviders } from '@app/client/resources/w3auth/types';
import { PathParams } from '@app/routes/types';
import { loginWithToken, setAuthTokens } from '@app/store/actions/auth';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

import { AddTwitter } from './AddTwitter';
import { AddTwitterSuccess } from './AddTwitterSuccess';
import { AuthFooter } from './AuthFooter';
import { AuthPanelStep } from './AuthPanelStep';
import { CombineAccounts } from './CombineAccounts';
import { EmailActivation } from './EmailActivation';
import { EntryPoint } from './EntryPoint';
import { ForgotPassword } from './ForgotPassword';
import { ForgotPasswordConfirmation } from './ForgotPasswordConfirmation';
import { ResetPassword } from './ResetPassword';
import { SignUpWithEmail } from './SignUpWithEmail';
import { TwoFactorAuth } from './TwoFactorAuth';
import { Web3AuthContext } from './Web3AuthContext';
import { AuthNewLocalStorage } from './enums';

import { AuthPanelSteps } from './styles';

enum AuthSearchParams {
    OAuthToken = 'oauth_token',
    OAuthVerifier = 'oauth_verifier',
    Code = 'code',
    State = 'state',
    Scope = 'scope',
    AuthUser = 'authuser',
    Hd = 'hd',
    Prompt = 'prompt',
}

const componentsByStep = {
    entry: EntryPoint,
    'add-twitter': AddTwitter,
    'add-twitter-success': AddTwitterSuccess,
    'sign-up-with-email': SignUpWithEmail,
    'reset-password': ResetPassword,
    'forgot-password': ForgotPassword,
    'forgot-password-confirmation': ForgotPasswordConfirmation,
    'combine-accounts': CombineAccounts,
    'email-activation': EmailActivation,
    'two-factor-auth': TwoFactorAuth,
};

export enum Web3AuthPanelRenderContext {
    Teams = 'teams-referral_fantasy-wishlist-lineup-page',
}

export interface Web3AuthPanelProps {
    renderCtx?: Web3AuthPanelRenderContext;
    onClose?: () => void;
}

interface RenderContextType {
    renderCtx?: Web3AuthPanelRenderContext;
}

const RenderContext = createContext<RenderContextType>({ renderCtx: undefined });
export const useWeb3RenderContext = () => useContext(RenderContext);

export function Web3AuthPanel({ onClose, renderCtx }: Web3AuthPanelProps) {
    const { currentStep, setCurrentStep } = useContext(Web3AuthContext);
    const CurrentStepComponent = componentsByStep[currentStep];
    const [searchParams, setSearchParams] = useSearchParams();
    const { [PathParams.AuthProvider]: authProvider } = useParams();
    const dispatch = useAppDispatch();
    const tokens = useAuthTokens((rootState) => rootState.token);

    const oAuthToken = searchParams.get(AuthSearchParams.OAuthToken);
    const oAuthVerifier = searchParams.get(AuthSearchParams.OAuthVerifier);
    const code = searchParams.get(AuthSearchParams.Code);
    const state = searchParams.get(AuthSearchParams.State);

    const clearSearchParams = useCallback(() => {
        Object.values(AuthSearchParams).forEach((searchParam) => searchParams.delete(searchParam));
        setSearchParams(searchParams);
    }, [searchParams, setSearchParams]);

    const handleAuthToken = useCallback(
        (token: Token) => {
            const twoFAShouldPrompt =
                (token?.requires_2fa && token?.requires_2fa !== TwoFactorAuthType.Invalid) || false;
            localStorage.setItem(AuthNewLocalStorage.OAuthLoggedIn, '1');
            if (twoFAShouldPrompt) {
                dispatch(setAuthTokens(token));
            } else {
                dispatch(loginWithToken(token));
            }
        },
        [dispatch],
    );

    useEffect(() => {
        (async () => {
            if (!oAuthToken || !oAuthVerifier || authProvider !== 'sign-in-with-twitter') return;
            setCurrentStep('add-twitter');
            const token = await callTwitterProviderSignIn({ oAuthToken, oAuthVerifier });
            clearSearchParams();
            setCurrentStep('add-twitter-success');
            handleAuthToken(token);
        })();
    }, [authProvider, oAuthToken, oAuthVerifier, setCurrentStep, clearSearchParams, handleAuthToken]);

    useEffect(() => {
        (async () => {
            let provider;
            switch (authProvider) {
                case 'sign-in-with-apple':
                    provider = AuthProviders.Apple;
                    break;
                case 'sign-in-with-discord':
                    provider = AuthProviders.Discord;
                    break;
                case 'sign-in-with-google':
                    provider = AuthProviders.Google;
                    break;
                default:
                    return;
            }

            if (!code || !state) return;
            const token: Token = await callGenericProviderSignIn(provider, { code, state });
            clearSearchParams();
            handleAuthToken(token);
        })();
    }, [authProvider, code, state, clearSearchParams, handleAuthToken]);

    useEffect(() => {
        const twoFAShouldPrompt = (tokens?.requires_2fa && tokens?.requires_2fa !== TwoFactorAuthType.Invalid) || false;
        if (twoFAShouldPrompt) {
            setCurrentStep('two-factor-auth');
        }
    }, [tokens, setCurrentStep]);

    function handleBack() {
        setCurrentStep('entry');
    }

    const headerPrefix =
        currentStep !== 'entry' ? (
            <ButtonBack color="secondary" variant="solid" icon={<ArrowLeft />} onClick={handleBack} />
        ) : null;

    return (
        <RenderContext.Provider value={{ renderCtx }}>
            <Sheet backdrop onClose={onClose}>
                <Sheet.Main>
                    <Sheet.Layout>
                        <Sheet.Layout.Columns stretch>
                            <Sheet.Layout.Columns.Primary padded scrollable>
                                <AuthPanelSteps>
                                    {headerPrefix}
                                    <AuthPanelStep key={currentStep}>
                                        <CurrentStepComponent />
                                    </AuthPanelStep>
                                </AuthPanelSteps>
                            </Sheet.Layout.Columns.Primary>
                        </Sheet.Layout.Columns>
                    </Sheet.Layout>
                </Sheet.Main>

                <Sheet.Footer>
                    <AuthFooter currentStep={currentStep} />
                </Sheet.Footer>
            </Sheet>
        </RenderContext.Provider>
    );
}
